// ==========================================================================
// App.tsx — picks the screen from the auth stage (FF-003), then routes
// inside the AppShell once signed in (FF-025).
// ==========================================================================
import { Loader2 } from "lucide-react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import AppShell from "@/components/AppShell";
import { Brand } from "@/components/Brand";
import { Button, Card } from "@/components/ui";
import Login from "@/screens/Login";
import MfaSetup from "@/screens/MfaSetup";
import MfaVerify from "@/screens/MfaVerify";
import Home from "@/screens/Home";
import Family from "@/screens/Family";
import Tickets from "@/screens/Tickets";
import Vehicles from "@/screens/Vehicles";
import Vehicle, { NewVehicle } from "@/screens/Vehicle";
import LogTrip from "@/screens/LogTrip";
import Wfh from "@/screens/Wfh";
import Calendar from "@/screens/Calendar";
import Money from "@/screens/Money";
import PropertyList from "@/screens/Property";
import Investments from "./screens/Investments";
import Kitchen from "@/screens/Kitchen";
import Us from "@/screens/Us";
import Journal from "@/screens/Journal";
import Bills from "@/screens/Bills";
import FamilyTree from "@/screens/FamilyTree";
import PropertyDetail from "@/screens/PropertyDetail";
import Profile from "@/screens/Profile";
import Admin from "@/screens/Admin";

function NoHousehold() {
  const { session, signOut } = useAuth();
  return (
    <main className="mx-auto max-w-md px-4 py-10 space-y-6">
      <Brand />
      <Card className="space-y-3">
        <p>You're signed in as <span className="font-medium">{session?.user.email}</span>, but that email isn't part of a household yet.</p>
        <Button variant="ghost" onClick={signOut}>Sign out</Button>
      </Card>
    </main>
  );
}

export default function App() {
  const { stage, householdIds } = useAuth();
  switch (stage) {
    case "loading":
      return <main className="flex min-h-screen items-center justify-center text-ink-muted"><Loader2 className="h-6 w-6 animate-spin" /></main>;
    case "signed-out":
      return <Login />;
    case "mfa-setup":
      return <MfaSetup />;
    case "mfa-verify":
      return <MfaVerify />;
    case "ready":
      if (householdIds.length === 0) return <NoHousehold />;
      return (
        <Routes>
          <Route element={<AppShell />}>
            <Route index element={<Home />} />
            <Route path="/family" element={<Family />} />
            <Route path="/family/tree" element={<FamilyTree />} />
            <Route path="/vehicles" element={<Vehicles />} />
            <Route path="/vehicles/new" element={<NewVehicle />} />
            <Route path="/vehicles/:id" element={<Vehicle />} />
            <Route path="/vehicles/:id/trip" element={<LogTrip />} />
            <Route path="/money/*" element={<Money />} />
            <Route path="/investments" element={<Investments />} />
            <Route path="/property" element={<PropertyList />} />
            <Route path="/property/:id" element={<PropertyDetail />} />
            <Route path="/calendar" element={<Calendar />} />
            <Route path="/kitchen/*" element={<Kitchen />} />
            <Route path="/us/*" element={<Us />} />
            <Route path="/journal" element={<Journal />} />
            <Route path="/bills" element={<Bills />} />
            <Route path="/wfh" element={<Wfh />} />
            <Route path="/tickets" element={<Tickets />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      );
  }
}
